"use client";

import { useEffect } from "react";

export type GlobalEmbedScript = {
  key: string;
  placement: "head" | "bodyStart" | "bodyEnd";
  src?: string;
  type?: string;
  async?: boolean;
  defer?: boolean;
  inlineCode?: string;
};

const ensureScriptElement = (script: GlobalEmbedScript): void => {
  const marker = `data-cms-embed-script-id="${script.key}"`;
  const existing = document.querySelector(
    `script[${marker}]`,
  ) as HTMLScriptElement | null;
  if (existing) return;

  const element = document.createElement("script");
  element.setAttribute("data-cms-embed-script-id", script.key);

  if (script.type) {
    element.type = script.type;
  }
  if (script.src) {
    element.src = script.src;
    if (script.async) element.async = true;
    if (script.defer) element.defer = true;
    element.setAttribute("data-embed-runtime-script", "true");
    element.addEventListener(
      "load",
      () => {
        element.setAttribute("data-loaded", "true");
      },
      { once: true },
    );
    element.addEventListener(
      "error",
      () => {
        element.setAttribute("data-error", "true");
      },
      { once: true },
    );
  } else if (script.inlineCode) {
    element.text = script.inlineCode;
  } else {
    return;
  }

  if (script.placement === "head") {
    document.head.appendChild(element);
    return;
  }

  if (script.placement === "bodyStart") {
    const first = document.body.firstChild;
    if (first) {
      document.body.insertBefore(element, first);
    } else {
      document.body.appendChild(element);
    }
    return;
  }

  document.body.appendChild(element);
};

export function GlobalEmbedScripts({
  scripts,
}: {
  scripts: GlobalEmbedScript[];
}) {
  useEffect(() => {
    for (const script of scripts) {
      ensureScriptElement(script);
    }
  }, [scripts]);

  return null;
}
